import * as THREE from 'three';

// Collects loose triangles (positions, normals, colours, uvs) and turns them
// into one BufferGeometry. Everything a tile draws in one material goes
// through one of these, so a tile costs only a few draw calls.
export class MeshBuilder {
  // glow: also record, for every vertex, the value of `lit` when it was added
  // (walls use it to say which windows shine at night).
  constructor(glow = false) {
    this.pos = [];
    this.nor = [];
    this.col = [];
    this.uv = [];
    this.glow = glow ? [] : null;
    this.lit = 0;
  }

  get empty() {
    return this.pos.length === 0;
  }

  vertex(x, y, z, nx, ny, nz, c, u, v) {
    this.pos.push(x, y, z);
    this.nor.push(nx, ny, nz);
    this.col.push(c.r, c.g, c.b);
    this.uv.push(u, v);
    if (this.glow) this.glow.push(this.lit);
  }

  // a, b, c are [x, y, z]; the normal comes from the winding (counter-clockwise front).
  tri(a, b, c, color, ua, ub, uc) {
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    this.vertex(a[0], a[1], a[2], nx, ny, nz, color, ua[0], ua[1]);
    this.vertex(b[0], b[1], b[2], nx, ny, nz, color, ub[0], ub[1]);
    this.vertex(c[0], c[1], c[2], nx, ny, nz, color, uc[0], uc[1]);
  }

  // a, b, c, d go round the quad; uv rect is [u0, v0, u1, v1] with a at (u0, v0),
  // b at (u1, v0), c at (u1, v1) and d at (u0, v1).
  quad(a, b, c, d, color, r) {
    this.tri(a, b, c, color, [r[0], r[1]], [r[2], r[1]], [r[2], r[3]]);
    this.tri(a, c, d, color, [r[0], r[1]], [r[2], r[3]], [r[0], r[3]]);
  }

  // The same quad with a and b in one colour and c and d in another: a wall darker at its foot.
  quad2(a, b, c, d, low, high, r) {
    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    this.vertex(a[0], a[1], a[2], nx, ny, nz, low, r[0], r[1]);
    this.vertex(b[0], b[1], b[2], nx, ny, nz, low, r[2], r[1]);
    this.vertex(c[0], c[1], c[2], nx, ny, nz, high, r[2], r[3]);
    this.vertex(a[0], a[1], a[2], nx, ny, nz, low, r[0], r[1]);
    this.vertex(c[0], c[1], c[2], nx, ny, nz, high, r[2], r[3]);
    this.vertex(d[0], d[1], d[2], nx, ny, nz, high, r[0], r[3]);
  }

  // The same vertices as typed arrays, ready to be handed to another thread.
  arrays() {
    return { pos: Float32Array.from(this.pos), nor: Float32Array.from(this.nor), col: Float32Array.from(this.col), uv: Float32Array.from(this.uv), lit: this.glow ? Float32Array.from(this.glow) : null };
  }

  geometry() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    if (this.glow) g.setAttribute('lit', new THREE.Float32BufferAttribute(this.glow, 1));
    return g;
  }
}
